import React, { Fragment } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'
import moment from 'moment'
import { Helmet } from 'react-helmet'
import queryString from 'query-string'

import {
  ENABLE_MONERIS_CHECKOUT,
  ENABLE_PAYPAL,
  PAYPAL_CURRENCY_CODE,
  PAYPAL_CLIENT_ID,
  VERB_ENV
} from '../../configuration/constants'
import appRoutes from '../../configuration/appRoutes'
// Note: session timer must be imported on the top of other UI component!
import SessionTimer from '../../components/SessionTimer/SessionTimer'

import Header from '../../components/Header'
import Layout from './Layout'
import GlobalModals from './GlobalModals'

import SessionExpiredModal from '../../components/SessionExpiredModal/SessionExpiredModal'
import SessionWarningModal from '../../components/SessionWarningModal/SessionWarningModal'

import { CHECKOUT_LIBRARY_URL } from '../../components/MonerisCheckout/constants'
import { updateConfigByLanguage } from '../../redux/configuration/actions'
import { ConfigProvider } from '../../redux/configuration/context'
import { selectFetchingLabels } from '../../redux/configuration/selectors'
import { selectLanguage } from '../../redux/session/selectors'
import ModalConsumer, { ModalProvider } from '../../context/modalContext'
import { UserProvider } from '../../context/userContext'
import { selectQueryParams } from '../../redux/selectors/selectQueryParams'
import { currentPathIsAny } from '../../utilities/browser/comparePaths'

if (VERB_ENV !== 'test') require('moment/min/locales.min')

const App = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const activeLanguage = useSelector(selectLanguage)
  const fetchingLabels = useSelector(selectFetchingLabels)
  const queryParams = useSelector(selectQueryParams)

  const handleRouteChange = (target, data = null) => {
    history.push({
      pathname: target,
      search: `?${queryParams}`,
      state: data
    })
    window.scrollTo(0, 0)
  }

  const handleToggleLanguage = languageCode => {
    if (languageCode && languageCode !== activeLanguage && !fetchingLabels) {
      const params = queryString.parse(window.location.search) || {}
      params.language = languageCode

      const search = new URLSearchParams()

      for (const [key, val] of Object.entries(params)) {
        search.append(key, val)
      }

      history.push({
        location: location.pathname,
        search: `?${search.toString()}`
      })
      moment.locale(languageCode)
      dispatch(updateConfigByLanguage({ languageCode }))
    }
  }

  const showExtendedHeader =
    !currentPathIsAny([
      appRoutes.register.pathname,
      appRoutes.account.pathname,
      appRoutes.telephoneBooking.pathname,
      appRoutes.forgotPassword.pathname
    ]) &&
    !location.pathname.includes(appRoutes.updatePassword.pathnameWithoutParams)

  // https://developer.paypal.com/docs/api/reference/locale-codes/
  const payPalLocale = activeLanguage === 'fr' ? 'fr_CA' : 'en_US'

  return (
    <Fragment>
      {ENABLE_MONERIS_CHECKOUT && (
        <Helmet>
          <script async src={CHECKOUT_LIBRARY_URL} />
        </Helmet>
      )}
      {ENABLE_PAYPAL && (
        <Helmet>
          <script
            defer
            src={`https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=${PAYPAL_CURRENCY_CODE}&locale=${payPalLocale}`}
          />
        </Helmet>
      )}
      <ConfigProvider>
        <UserProvider>
          <ModalProvider>
            <ModalConsumer>
              {({
                modalIsOpen,
                openModal,
                closeModal,
                modalContinue,
                changeModals
              }) => (
                <div
                  className="ferries-create-reservation"
                  data-testid="ferries-create-reservation"
                >
                  <Header
                    handleRouteChange={handleRouteChange}
                    openModal={openModal}
                    showExtendedHeader={showExtendedHeader}
                    handleToggleLanguage={handleToggleLanguage}
                  />
                  <Layout
                    pathname={location.pathname}
                    handleRouteChange={handleRouteChange}
                    handleToggleLanguage={handleToggleLanguage}
                    openModal={openModal}
                    closeModal={closeModal}
                    changeModals={changeModals}
                    modalIsOpen={modalIsOpen}
                    showExtendedHeader={showExtendedHeader}
                  />
                  {/* session timer must be inside the app component directly! */}
                  <SessionTimer
                    handleRouteChange={handleRouteChange}
                    render={({
                      sessionTimeoutCounter,
                      showWarningModal,
                      showExpiredModal,
                      continueBookingFromWarningModal,
                      continueBookingFromExpiredModal
                    }) => (
                      <Fragment>
                        <SessionWarningModal
                          isOpen={showWarningModal}
                          countdown={sessionTimeoutCounter}
                          onContinueBookingClick={
                            continueBookingFromWarningModal
                          }
                        />
                        <SessionExpiredModal
                          isOpen={showExpiredModal}
                          onContinueBookingClick={
                            continueBookingFromExpiredModal
                          }
                        />
                      </Fragment>
                    )}
                  />
                  <GlobalModals
                    handleRouteChange={handleRouteChange}
                    modalIsOpen={modalIsOpen}
                    openModal={openModal}
                    closeModal={closeModal}
                    modalContinue={modalContinue}
                  />
                </div>
              )}
            </ModalConsumer>
          </ModalProvider>
        </UserProvider>
      </ConfigProvider>
    </Fragment>
  )
}

export default App
